import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	FormControl,
	FormErrorMessage,
	FormLabel,
	InputGroup,
	InputLeftElement,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	NumberInput,
	NumberInputField,
	Text,
	VStack,
	useDisclosure
} from "@chakra-ui/react";
import { Select as ChakraReactSelect } from "chakra-react-select";
import { FunctionComponent, ReactElement, useEffect, useMemo, useRef, useState } from "react";

// Utils
import { GroupSelectOption } from "../../../../utils/types";

interface AddRateModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (benefitCode: string, hourlyRate: string, flatRate: string) => Promise<void>;
	existingBenefitCodes: string[];
	benefitOptions: GroupSelectOption[];
	isLoadingBenefits: boolean;
	editingRate?: {
		benefitCode: string;
		standardHourlyRate: string;
		standardFlatRate: string;
	};
}

const acceptedDecimalInput = new RegExp(/^[0-9.]$/);

/**
 * Modal for adding or editing a provider rate
 */
const AddRateModal: FunctionComponent<AddRateModalProps> = (props: AddRateModalProps): ReactElement => {
	const { isOpen, onClose, onSave, existingBenefitCodes, benefitOptions, isLoadingBenefits, editingRate } = props;

	// Form state
	const [benefitCode, setBenefitCode] = useState<string>("");
	const [benefitLabel, setBenefitLabel] = useState<string>("");
	const [hourlyRate, setHourlyRate] = useState<string>("");
	const [flatRate, setFlatRate] = useState<string>("");
	const [isSaving, setIsSaving] = useState<boolean>(false);

	// Track initial values for dirty checking
	const [initialValues, setInitialValues] = useState<{
		benefitCode: string;
		hourlyRate: string;
		flatRate: string;
	}>({ benefitCode: "", hourlyRate: "", flatRate: "" });

	// Unsaved changes dialog
	const { isOpen: isUnsavedDialogOpen, onOpen: onUnsavedDialogOpen, onClose: onUnsavedDialogClose } = useDisclosure();
	const cancelRef = useRef<HTMLButtonElement>(null);

	// Validation state
	const [errors, setErrors] = useState<{
		benefitCode?: string;
		rates?: string;
	}>({});

	// Check if form has unsaved changes
	const hasUnsavedChanges = useMemo(() => {
		return (
			benefitCode !== initialValues.benefitCode ||
			hourlyRate !== initialValues.hourlyRate ||
			flatRate !== initialValues.flatRate
		);
	}, [benefitCode, hourlyRate, flatRate, initialValues]);

	// Reset form when modal opens/closes or editingRate changes
	useEffect(() => {
		if (isOpen) {
			if (editingRate) {
				setBenefitCode(editingRate.benefitCode);
				setBenefitLabel(editingRate.benefitCode);
				setHourlyRate(editingRate.standardHourlyRate || "");
				setFlatRate(editingRate.standardFlatRate || "");
				setInitialValues({
					benefitCode: editingRate.benefitCode,
					hourlyRate: editingRate.standardHourlyRate || "",
					flatRate: editingRate.standardFlatRate || ""
				});
			} else {
				setBenefitCode("");
				setBenefitLabel("");
				setHourlyRate("");
				setFlatRate("");
				setInitialValues({ benefitCode: "", hourlyRate: "", flatRate: "" });
			}
			setErrors({});
		}
	}, [isOpen, editingRate]);

	// Find the selected option for the dropdown
	const selectedOption = useMemo(() => {
		if (!benefitCode) return null;
		for (const group of benefitOptions) {
			const found = group.options?.find((opt) => opt.value === benefitCode);
			if (found) return found;
		}
		// If not found in options, return a basic option with just the code
		return benefitCode ? { label: benefitLabel || benefitCode, value: benefitCode } : null;
	}, [benefitCode, benefitLabel, benefitOptions]);

	// Filter out already existing benefit codes (except when editing the same code)
	const filteredOptions = useMemo(() => {
		return benefitOptions.map((group) => ({
			...group,
			options: group.options?.filter(
				(opt) => !existingBenefitCodes.includes(opt.value) || opt.value === editingRate?.benefitCode
			)
		}));
	}, [benefitOptions, existingBenefitCodes, editingRate]);

	const validateForm = (): boolean => {
		const newErrors: typeof errors = {};

		if (!benefitCode.trim()) {
			newErrors.benefitCode = "Please select a benefit code";
		} else if (existingBenefitCodes.includes(benefitCode) && benefitCode !== editingRate?.benefitCode) {
			newErrors.benefitCode = "You already have a rate for this benefit code";
		}

		if (!hourlyRate && !flatRate) {
			newErrors.rates = "Please enter at least one rate (hourly or flat)";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async () => {
		if (!validateForm()) return;

		setIsSaving(true);
		try {
			await onSave(benefitCode, hourlyRate, flatRate);
			onClose();
		} catch (error) {
			// Error handling is done in the parent component
		} finally {
			setIsSaving(false);
		}
	};

	const handleClose = () => {
		if (hasUnsavedChanges) {
			onUnsavedDialogOpen();
		} else {
			onClose();
		}
	};

	const handleConfirmClose = () => {
		onUnsavedDialogClose();
		onClose();
	};

	const handleBenefitChange = (option: { label: string; value: string } | null) => {
		if (option) {
			setBenefitCode(option.value);
			setBenefitLabel(option.label);
		} else {
			setBenefitCode("");
			setBenefitLabel("");
		}
		// Clear the benefit code error when user selects
		if (errors.benefitCode) {
			setErrors((prev) => ({ ...prev, benefitCode: undefined }));
		}
	};

	const handleRateChange = (setter: (value: string) => void) => (valueString: string) => {
		// Remove any non-numeric characters except decimal point
		const cleanValue = valueString.replace(/[^0-9.]/g, "");
		// Ensure only one decimal point
		const parts = cleanValue.split(".");
		let formatted = parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : cleanValue;
		// Limit to 2 decimal places
		if (parts.length === 2 && parts[1].length > 2) {
			formatted = parts[0] + "." + parts[1].slice(0, 2);
		}
		setter(formatted);
		// Clear the rates error when user enters a value
		if (errors.rates) {
			setErrors((prev) => ({ ...prev, rates: undefined }));
		}
	};

	const formatCurrencyDisplay = (val: string): string => {
		if (!val || val === "0") return "";
		// Format with commas for display
		const num = parseFloat(val);
		if (isNaN(num)) return val;
		// Only format if it's a complete number (not ending with decimal)
		if (val.endsWith(".")) return val;
		// Format with up to 2 decimal places
		const parts = val.split(".");
		const intPart = parseInt(parts[0]).toLocaleString();
		if (parts.length === 2) {
			return `${intPart}.${parts[1]}`;
		}
		return intPart;
	};

	const parseCurrencyInput = (val: string): string => {
		// Remove commas and other formatting
		return val.replace(/,/g, "");
	};

	const isEditing = !!editingRate;

	return (
		<>
			<Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered closeOnOverlayClick={false}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{isEditing ? "Edit Rate" : "Add New Rate"}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<VStack spacing={5} align="stretch">
							{/* Benefit Code Dropdown */}
							<FormControl isInvalid={!!errors.benefitCode} isDisabled={isEditing}>
								<FormLabel fontWeight="500" fontSize="sm" color="gray.600">
									Benefit Code
								</FormLabel>
								<ChakraReactSelect
									options={filteredOptions}
									placeholder="Search by code or name..."
									value={selectedOption}
									onChange={handleBenefitChange}
									isDisabled={isEditing || isLoadingBenefits}
									isLoading={isLoadingBenefits}
									isClearable
									isSearchable
									size="sm"
									menuPosition="fixed"
									menuShouldBlockScroll={true}
									maxMenuHeight={280}
									chakraStyles={{
										control: (provided) => ({
											...provided,
											fontSize: "0.9rem",
											borderColor: errors.benefitCode ? "red.500" : "gray.300"
										}),
										menu: (provided) => ({ ...provided, zIndex: 10 }),
										groupHeading: (provided) => ({
											...provided,
											fontSize: "0.75rem",
											fontWeight: "600",
											color: "gray.500",
											textTransform: "uppercase"
										})
									}}
								/>
								<FormErrorMessage>{errors.benefitCode}</FormErrorMessage>
							</FormControl>

							{/* Standard Hourly Rate */}
							<FormControl isInvalid={!!errors.rates}>
								<FormLabel fontWeight="500" fontSize="sm" color="gray.600">
									Standard Hourly Rate
								</FormLabel>
								<InputGroup size="sm">
									<InputLeftElement pointerEvents="none" color="gray.500" fontSize="sm" h="full">
										$
									</InputLeftElement>
									<NumberInput
										size="sm"
										min={0}
										precision={2}
										value={formatCurrencyDisplay(hourlyRate)}
										onChange={(valueString) => handleRateChange(setHourlyRate)(parseCurrencyInput(valueString))}
										parse={parseCurrencyInput}
										isValidCharacter={(value) => acceptedDecimalInput.test(value) || value === ","}
										w="100%">
										<NumberInputField pl="8" borderRadius="md" placeholder="0.00" />
									</NumberInput>
								</InputGroup>
							</FormControl>

							{/* Standard Flat Rate */}
							<FormControl isInvalid={!!errors.rates}>
								<FormLabel fontWeight="500" fontSize="sm" color="gray.600">
									Standard Flat Rate
								</FormLabel>
								<InputGroup size="sm">
									<InputLeftElement pointerEvents="none" color="gray.500" fontSize="sm" h="full">
										$
									</InputLeftElement>
									<NumberInput
										size="sm"
										min={0}
										precision={2}
										value={formatCurrencyDisplay(flatRate)}
										onChange={(valueString) => handleRateChange(setFlatRate)(parseCurrencyInput(valueString))}
										parse={parseCurrencyInput}
										isValidCharacter={(value) => acceptedDecimalInput.test(value) || value === ","}
										w="100%">
										<NumberInputField pl="8" borderRadius="md" placeholder="0.00" />
									</NumberInput>
								</InputGroup>
								{errors.rates && <FormErrorMessage>{errors.rates}</FormErrorMessage>}
							</FormControl>

							{/* Helper text */}
							<Box bg="gray.50" p={3} borderRadius="md">
								<Text fontSize="xs" color="gray.600">
									These rates will be used as your default standard rates when creating claims for this benefit type.
								</Text>
							</Box>
						</VStack>
					</ModalBody>

					<ModalFooter gap={3}>
						<Button variant="ghost" onClick={handleClose} size="sm">
							Cancel
						</Button>
						<Button colorScheme="orange" onClick={handleSave} isLoading={isSaving} size="sm">
							{isEditing ? "Save Changes" : "Add Rate"}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Unsaved Changes Confirmation Dialog */}
			<AlertDialog
				isOpen={isUnsavedDialogOpen}
				leastDestructiveRef={cancelRef}
				onClose={onUnsavedDialogClose}
				isCentered>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							Unsaved Changes
						</AlertDialogHeader>

						<AlertDialogBody>You have unsaved changes. Are you sure you want to close without saving?</AlertDialogBody>

						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={onUnsavedDialogClose} size="sm">
								Keep Editing
							</Button>
							<Button colorScheme="red" onClick={handleConfirmClose} ml={3} size="sm">
								Discard Changes
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
};

export default AddRateModal;
